import { FC, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIButton, SCUILoadingLinear } from '../../../../../atomic/atoms';
import { ReturnCommand } from '../../../../../services/command';
import { RefundRestQuery } from '../../../../../services/query';
import { enumRefundReason, enumRefundStatusType } from '../../../../../types/enums/status';
import Helper from '../../../../../utils/helper';
import { resolveCloseRefundButtonContent } from './CloseRefundRecordButton.helpers';

export interface CloseRefundRecordButtonProps {
    refundId?: any;
    orderId: any;
    returnId?: any;
    customerId?: any;
    orderDate?: any;
    refundDetails: any;
    returnDatas?: any;
    manuelMethodId?: number;
    fullwidth?: boolean;
}

export const CloseRefundRecordButton: FC<CloseRefundRecordButtonProps> = ({
    refundId,
    orderId,
    returnId,
    customerId,
    orderDate,
    refundDetails,
    returnDatas,
    manuelMethodId = 0,
    fullwidth = false,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [visibleDoRefundMethod, setVisibleDoRefundMethod] = useState(false);
    const [returnedProductItemProcesses, setReturnedProductItemProcesses] = useState<any>([]);

    const unClosedRefundInitial = useMemo(
        () =>
            refundDetails?.entity.refundDetails.filter(
                (detail: any) => detail.refundDetailStatusId === 1 || detail.refundDetailStatusId === 3
            ),
        [refundDetails]
    );

    const refundStatus = refundDetails && enumRefundStatusType[refundDetails.entity.refundStatusId];
    const refundReason = refundDetails && enumRefundReason[refundDetails.entity.refundReasonId];

    useEffect(() => {
        if (returnDatas?.productDetailModels) {
            setReturnedProductItemProcesses(
                returnDatas.productDetailModels.map((item: any) => {
                    return {
                        returnItemId: item.returnItemId,
                        productId: item.productId,
                        barcode: item.barcode,
                        quantity: item.piece,
                        restockQuantity: item.restockQuantity === 0 ? item.piece : item.restockQuantity,
                        nonRestockQuantity: item.nonRestockQuantity,
                        rejectedQuantity: item.rejectedQuantity,
                        operatorReturnReasonId: item.operatorReturnReasonId ? item.operatorReturnReasonId : 0,
                        rejectedReason: item.reason,
                        statusId: item.status,
                        shippingListId: item.shippingListId,
                    };
                })
            );
        }
    }, [returnDatas]);

    useEffect(() => {
        if (unClosedRefundInitial && unClosedRefundInitial.length > 0) {
            setVisibleDoRefundMethod(true);
        }
    }, [unClosedRefundInitial]);

    const DoRefund = async () => {
        setLoading(true);
        const creditCardManuelDataList = unClosedRefundInitial.map((item: any) => {
            return {
                RefundDetailId: item.refundDetailId,
                ...(item.refundMethodId === 2 && { ManuelMethodId: manuelMethodId }),
            };
        });
        const params: any = {
            refundId: refundId,
            orderDate: orderDate ? orderDate : null,
        };
        if (manuelMethodId === 2) {
            params.CreditCardManuelDataList = creditCardManuelDataList;
        }

        RefundRestQuery.DoRefundPendings(params)
            .then((res: any) => {
                if (res) {
                    window.location.reload();
                } else {
                    Swal.fire(t('refund.refundSentToBank'), ' ', 'success');
                }
                setLoading(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const CompleteAndConfirmReturn = async () => {
        setLoading(true);
        const params: any = {};
        params.orderId = orderId;
        params.returnId = returnId;
        params.customerID = customerId;
        params.storeCode = Helper.getStoreCode();
        params.tryDoRefund = true;
        params.creditCardRefundManuel = manuelMethodId === 2;
        params.returnedProductItemProcesses = returnedProductItemProcesses;
        if (manuelMethodId === 2) {
            params.creditCardManuelDataList = unClosedRefundInitial.map((item: any) => {
                return {
                    refundDetailId: item.refundDetailId,
                    manuelMethodId: manuelMethodId,
                };
            });
        }

        try {
            const res: any = await ReturnCommand.CompleteAndConfirmReturn(params);
            if (res.returnedProductProcess?.result) {
                window.location.reload();
            } else {
                Swal.fire({
                    title: t('general.failed'),
                    text: res.returnedProductProcess?.returnMessages,
                    icon: 'error',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const closeRefundButton = (onClick: () => void) => (
        <Grid
            size={{
                xl: 12,
                lg: 12,
                md: 12,
                xs: 12,
            }}
        >
            <SCUIButton
                text={t('refund.closeRefundRecord')}
                type="primary"
                sizeType={fullwidth ? 'default' : 'fixed'}
                size={'l'}
                buttonType="button"
                onClick={onClick}
                fullwidth={fullwidth}
            />
        </Grid>
    );

    const buttonContent = resolveCloseRefundButtonContent({
        refundStatus,
        refundReason,
        visibleDoRefundMethod,
        refundDetails,
        closeRefundButton,
        onCompleteAndConfirmReturn: CompleteAndConfirmReturn,
        onDoRefund: DoRefund,
    });

    if (!buttonContent) {
        return null;
    }

    return (
        <>
            <SCUILoadingLinear display={loading} />
            {buttonContent}
        </>
    );
};
